import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useCategorySummaryStore } from "@/components/ui/category-summary/hooks/useCategorySummaryStore";
import { useAnalyses } from "@/hooks/useAnalyses";
import { useCategoryTree } from "@/hooks/useCategoryTree";

export const useAnalysisCategorySelect = (
  isMainPanel: boolean,
  limit: number = 10,
) => {
  const { completedAnalyses: analyses } = useAnalyses(limit);
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    mainSelectedAnalysisId,
    mainSelectedCategoryPath,
    compSelectedAnalysisId,
    compSelectedCategoryPath,
    setMainSelectedAnalysisId,
    setCompSelectedAnalysisId,
    setMainSelectedCategoryPath,
    setCompSelectedCategoryPath,
  } = useCategorySummaryStore();

  const selectedAnalysisId = isMainPanel
    ? mainSelectedAnalysisId
    : compSelectedAnalysisId;
  const selectedCategoryPath = isMainPanel
    ? mainSelectedCategoryPath
    : compSelectedCategoryPath;
  const setSelectedAnalysisId = isMainPanel
    ? setMainSelectedAnalysisId
    : setCompSelectedAnalysisId;
  const setSelectedCategoryPath = isMainPanel
    ? setMainSelectedCategoryPath
    : setCompSelectedCategoryPath;

  const [isOpenCategorySelect, setIsOpenCategorySelect] = useState(false);

  // 以下、仕様として比較先のパネルではリロード時に前回の状態を再現しない方針とする（比較元の状態を比較先の状態と同一にする）
  const analysisKey = isMainPanel ? "analysis" : "";
  const categoryKey = isMainPanel ? "category" : "";
  const searchParamAnalysisId = searchParams.get(analysisKey);
  const searchParamCategoryPath = searchParams.get(categoryKey);

  const selectedAnalysis = analyses?.find((a) => a.id === selectedAnalysisId);
  const { categoryTree } = useCategoryTree(selectedAnalysis?.categoryTreeId);

  // 分析のセレクトボックスのオプション
  const analysisSelectOptions = analyses
    ?.filter((analysis) => analysis.status === "COMPLETED")
    .map((analysis) => ({ value: analysis.id, label: analysis.title })) ?? [];

  // 選択された分析IDの値が不正な場合はリセット (URLパラメータに不正な値が入っている場合など)
  useEffect(() => {
    if (!analyses?.length) return;
    // 比較パネルの場合はURLからの初期化をスキップ。
    if (!isMainPanel) return;

    const analysisId = searchParamAnalysisId
        && analyses.map((a) => a.id).includes(searchParamAnalysisId)
      ? searchParamAnalysisId
      : "";
    setSelectedAnalysisId(analysisId);
  }, [analyses?.length, searchParamAnalysisId]);

  // 選択されたカテゴリの値が不正な場合はリセット (URLパラメータに不正な値が入っている場合など)
  useEffect(() => {
    if (!categoryTree) return;
    // 比較パネルの場合はURLからの初期化をスキップ。
    if (!isMainPanel) return;

    const path = searchParamCategoryPath
        && categoryTree.filterCategoriesByPaths([searchParamCategoryPath]).length
      ? searchParamCategoryPath
      : "";
    setSelectedCategoryPath(path);
  }, [categoryTree?.id, searchParamCategoryPath]);

  const handleAnalysisChange = (value: string) => {
    if (isMainPanel) {
      setSearchParams((searchParams) => {
        searchParams.set(analysisKey, value);
        return searchParams;
      });
    } else {
      // 比較パネルの場合はURLを更新せず内部状態のみ更新
      setSelectedAnalysisId(value);
    }
  };

  const handleCategoryChange = (value: string) => {
    if (isMainPanel) {
      setSearchParams((searchParams) => {
        if (!selectedAnalysisId) return searchParams;

        searchParams.set(analysisKey, selectedAnalysisId);
        searchParams.set(categoryKey, value);
        return searchParams;
      });
    } else {
      // 比較パネルの場合は内部状態のみ更新
      setSelectedCategoryPath(value);
    }
  };

  const handleOpenCategorySelect = () => {
    setIsOpenCategorySelect(true);
  };

  const handleCloseCategorySelect = () => {
    setIsOpenCategorySelect(false);
  };

  return {
    selectedAnalysisId,
    selectedCategoryPath,
    analysisSelectOptions,
    isOpenCategorySelect,
    categoryTree,
    handleAnalysisChange,
    handleCategoryChange,
    handleOpenCategorySelect,
    handleCloseCategorySelect,
  };
};
